import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { dialogManager } from '../stores/DialogManager';
import { layoutInformation } from '../submodules/xbox/utils/Layout';

import { DialogType } from '@egr/xbox/egr-gui-dialogs/Constants';
import Dialog from '@egr/xbox/egr-gui-dialogs/utils/DialogOverride';
import { DialogContent } from '@egr/xbox/egr-gui-dialogs/whiteDialog/DialogContent';
import { DialogTitle } from '@egr/xbox/egr-gui-dialogs/whiteDialog/DialogTitle';
import { DialogTransition } from '@egr/xbox/egr-gui-elements/single-components/DialogTransition';
import { downloadBlob } from '@egr/xbox/utils/Download';
import { ios } from '@egr/xbox/utils/UserAgent';
import { makeStyles } from '@egr/xbox/utils/tss-react';

import { isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';
interface FullscreenHTMLElement extends HTMLImageElement {
    mozRequestFullScreen?: () => void;
    msRequestFullscreen?: () => void;
    webkitRequestFullscreen?: () => void;
}

const useSnapshotDialogStyles = makeStyles({ name: 'SnapshotDialog' })((theme) => ({
    content: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
        overflow: 'hidden',
    },
    contentFullscreen: {
        padding: theme.spacing(3),
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
    },
    imageWrapper: {
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    image: {
        maxWidth: '100%',
        maxHeight: '100%',
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        display: 'block',
    },
    fullscreenIcon: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(1),
        cursor: 'pointer',
        width: 24,
        height: 24,
    },
    imageFullscreen: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        position: 'relative',
    },
}));
export const SnapshotDialog: React.FC = () => {
    const { classes, cx } = useSnapshotDialogStyles();
    const [t] = useTranslation();
    const { snapShotDialog } = dialogManager.useState(['snapShotDialog']);
    const { smallLayout } = layoutInformation.useState(['smallLayout']);
    const fullscreenElement = React.useRef<FullscreenHTMLElement>(null);

    const snapshot = snapShotDialog?.snapshot;
    const src = snapshot ? URL.createObjectURL(snapshot) : '';
    React.useEffect(() => {
        return () => {
            if (isNotNullOrEmpty(src)) {
                URL.revokeObjectURL(src);
            }
        };
    }, [src]);

    if (snapShotDialog == null) return null;
    const { title } = snapShotDialog;
    const showfullscreen = async (): Promise<void> => {
        if (fullscreenElement.current) {
            if (fullscreenElement.current.requestFullscreen) {
                await fullscreenElement.current.requestFullscreen();
            } else if (fullscreenElement.current.mozRequestFullScreen) {
                fullscreenElement.current.mozRequestFullScreen();
            } else if (fullscreenElement.current.webkitRequestFullscreen) {
                fullscreenElement.current.webkitRequestFullscreen();
            }
        }
    };
    const save = (): void => {
        if (snapshot) {
            downloadBlob(snapshot, 'impress.jpg');
        }
    };
    function onClose(): void {
        dialogManager.closeDialog(DialogType.SNAPSHOT);
    }
    return (
        <Dialog
            onClose={onClose}
            open={true}
            TransitionComponent={DialogTransition}
            fullScreen={smallLayout}
        >
            <DialogTitle
                shouldClose
                back={onClose}
            >
                {title}
            </DialogTitle>
            <DialogContent
                className={cx(classes.content, smallLayout && classes.contentFullscreen)}
                dividers
            >
                <div className={classes.imageWrapper}>
                    <img
                        src={src}
                        ref={fullscreenElement}
                        className={cx(classes.image, smallLayout && classes.imageFullscreen)}
                    />
                    {!ios && (
                        <img
                            src="images/ic_fullscreen_black_24px.svg"
                            className={classes.fullscreenIcon}
                            onClick={showfullscreen}
                        />
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={save}>{t('Save')}</Button>
            </DialogActions>
        </Dialog>
    );
};

SnapshotDialog.displayName = 'SnapshotDialog';
